<template>
    <section class="bg-[#17364a] min-h-screen flex items-center overflow-hidden">
        <div class="mx-auto max-w-7xl px-6 lg:px-8 py-24 lg:py-32 w-full">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <!-- Left: content -->
                <div class="relative z-10">
                    <span class="inline-block text-xs font-semibold uppercase tracking-widest text-[#A07830] mb-6 border border-[#A07830]/30 px-3 py-1">
                        {{ data.subtitle }}
                    </span>
                    <h1 class="text-5xl sm:text-6xl xl:text-7xl font-extrabold leading-[1.05] text-white tracking-tight">
                        {{ data.titleLine1 }}<br />
                        <span class="gold-underline text-[#A07830]">{{ data.titleHighlight }}</span><br />
                        {{ data.titleLine2 }}
                    </h1>
                    <p class="mt-8 text-lg text-gray-400 leading-relaxed max-w-xl">
                        {{ data.description }}
                    </p>
                    <div class="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            @click="bookDemoModal = true"
                            class="inline-flex items-center px-7 py-3.5 text-sm font-semibold bg-white text-[#17364a] hover:bg-[#A07830] hover:text-white transition-colors duration-200"
                        >
                            {{ data.buttonText }}
                        </button>
                        <VideoButton />
                    </div>

                    <!-- Stats row -->
                    <div class="mt-14 flex items-center gap-10 border-t border-[#1e4d63] pt-8">
                        <div v-for="stat in heroStats" :key="stat.label">
                            <div class="text-2xl font-bold text-white">{{ stat.value }}</div>
                            <div class="text-xs text-gray-500 mt-0.5">{{ stat.label }}</div>
                        </div>
                    </div>
                </div>

                <!-- Right: image -->
                <div class="relative flex justify-center lg:justify-end">
                    <div class="relative">
                        <!-- Gold ring -->
                        <div class="absolute inset-0 rounded-full border-2 border-[#A07830]/30 scale-110"></div>
                        <img
                            src="/images/home/header.jpg"
                            alt="Nobel Academy"
                            class="relative w-80 h-80 lg:w-[420px] lg:h-[420px] object-cover object-center rounded-full"
                        />
                        <!-- Floating badge -->
                        <div class="absolute -bottom-4 -left-4 bg-white px-4 py-3 border-l-2 border-[#A07830]">
                            <div class="text-xs font-semibold uppercase text-[#A07830] tracking-widest">Expert</div>
                            <div class="text-sm font-bold text-[#17364a]">Certified Tutors</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Demo modal -->
        <Dialog v-model:visible="bookDemoModal" modal header="Request a Free Demo" :style="{ width: '32rem' }">
            <div class="flex items-center gap-4 mb-4">
                <label for="demo-email" class="font-semibold w-16 text-sm">Email</label>
                <InputText v-model="email" id="demo-email" class="flex-auto" autocomplete="email" placeholder="your@email.com" />
            </div>
            <div class="flex justify-end gap-2">
                <Button type="button" label="Cancel" severity="secondary" @click="bookDemoModal = false" />
                <Button type="button" label="Request Demo" @click="requestDemo" />
            </div>
        </Dialog>
    </section>
</template>

<script setup>
import { ref } from 'vue';
import VideoButton from '@/components/buttons/VideoButton.vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { requestDemoService } from '@/services/requestDemoService';

const props = defineProps(['title', 'image']);
const bookDemoModal = ref(false);
const email = ref('');

const data = {
    subtitle: 'Welcome to Nobel Academy',
    titleLine1: 'Expand Your',
    titleHighlight: 'Knowledge',
    titleLine2: 'Online.',
    description: 'Join thousands of students learning from expert tutors across a wide range of subjects. Flexible scheduling, interactive lessons, and a world-class experience.',
    buttonText: 'Book a Free Demo',
};

const heroStats = [
    { value: '1M+', label: 'Students Enrolled' },
    { value: '500+', label: 'Courses Available' },
    { value: '5', label: 'Languages' },
];

const requestDemo = () => {
    requestDemoService({ email: email.value })
        .then(() => { bookDemoModal.value = false; })
        .catch(() => { alert('There was an error. Please try again later.'); });
};
</script>
